
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import { Check, Share2, Copy, Twitter, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { chain } from '@/utils/Constant';

const Confirm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Get data passed from the homepage
  const amount = location.state?.amount || '';
  const destinationAddress = location.state?.destinationAddress || '';
  const multiDestinations = location.state?.multiDestinations || [];
  const txn = location.state?.deposit || '';
  const isMulti = location.state?.isMulti || false;
  
  useEffect(() => {
    // If no data was passed, redirect to home
    if (!amount || (isMulti && multiDestinations.length === 0) || (!isMulti && !destinationAddress)) {
      navigate('/');
      return;
    }
  }, [amount, destinationAddress, multiDestinations, isMulti, navigate]);
  
  const shareableLink = "https://whirlpoolswap.com";
  const twitterText = "I just used WhirlPool to anonymize my Solana transactions! 🌊 Privacy-first crypto made easy.";
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareableLink);
    toast({
      title: "Link copied!",
      description: "Share WhirlPool with your friends",
    });
  };
  
  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(shareableLink)}`;
    window.open(twitterUrl, '_blank');
  };
  
  const handleBackHome = () => {
    navigate('/');
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-lg glass-panel p-8 text-center">
          {/* Success Icon */}
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center">
              <Check className="h-10 w-10 text-white" />
            </div>
          </div>
          
          {/* Success Message */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent mb-4">
              Transaction Complete!
            </h1>
            <p className="text-lg text-muted-foreground mb-2">
              Your {amount} SOL has been successfully anonymized
            </p>
            <p className="text-sm text-muted-foreground">
              Thanks for using WhirlPool! 🌊
            </p>
          </div>
          
          {/* Transaction Summary */}
          <div className="bg-secondary rounded-lg p-6 border border-white/10 mb-8">
            <h3 className="text-lg font-semibold mb-4">Transaction Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount sent:</span>
                <span className="font-medium">{amount} SOL</span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted-foreground">Txn:</span>
                <span className="font-medium cursor-pointer" 
                onClick={() => window.open(`https://solscan.io/tx/${txn}?cluster=${chain}`, '_blank')}
                >{txn.slice(0, 6)}...{txn.slice(-4)}</span>
              </div>
              
              {isMulti ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Destinations:</span>
                  <span className="font-medium">{multiDestinations.length} wallets</span>
                </div>
              ) : (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Destination:</span>
                  <span className="font-medium">{destinationAddress.slice(0, 6)}...{destinationAddress.slice(-4)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-green-400">
                <span>Status:</span>
                <span className="font-medium">✓ Completed</span>
              </div>
            </div>
          </div>
          
          {/* Share Section */}
          <div className="bg-black/30 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center justify-center">
              <Share2 className="h-5 w-5 mr-2" />
              Share WhirlPool
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Help others discover privacy-first crypto transactions
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="flex-1 bg-secondary hover:bg-white/10 border-white/10"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
              
              <Button
                onClick={handleTwitterShare}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Twitter className="h-4 w-4 mr-2" />
                Tweet
              </Button>
            </div>
          </div>
          
          {/* Actions */}
          <div className="space-y-3">
            <Button 
              onClick={handleBackHome}
              className="w-full bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue"
            >
              Start New Transaction
            </Button>
            
            <Button 
              variant="ghost"
              className="w-full text-muted-foreground hover:text-white"
              onClick={() => window.open('https://docs.whirlpoolswap.com', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Learn More About Privacy
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Confirm;
